import { AxiosError } from 'axios'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import React from 'react'
import * as yup from 'yup'
import { Batch, BatchType } from 'src/api/models'
import { useQueryPaginator } from 'src/components/Table'
import { DateField, MapSelectField, QuerySelectField, TextField, useForm } from 'src/forms'
import { setApiErrors } from 'src/forms/errors'
import { skuit } from 'src/services/skuit'
import { handleErrorMessage, Notification } from 'src/components/Notification'
import toast from 'react-hot-toast'

const useStyles = makeStyles(() => ({
  form: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
}))

interface BatchProps {
  client: string
  batch_type: BatchType // eslint-disable-line camelcase
  client_uid: string // eslint-disable-line camelcase
  expires_on: string // eslint-disable-line camelcase
  asn: string
}

const batchSchema = yup.object().shape({
  batch_type: yup.string().required().label('Type'),
  client_uid: yup.string().required().label('Number'),
  expires_on: yup.date().optional().label('Expires On'),
  asn: yup.string().asn().optional().label('ASN'),
})

interface Props {
  open: boolean
  handleClose: () => void
  onSave?: (obj: any) => void
  update?: boolean
  batch?: Batch
  allowSelectASN?: boolean
}

export const BatchDialog = (props: Props) => {
  const { open, handleClose, onSave, update, batch, allowSelectASN = true } = props
  const classes = useStyles()
  const { Form, data: formData } = useForm(batchSchema)

  const asnPaginator = useQueryPaginator('asn-list', skuit.client.asn.list)

  const onSubmit = (data: BatchProps) => {
    let func = skuit.client.batches.create
    let action = 'creat'
    if (update) {
      func = (data: BatchProps) => skuit.client.batches.patch(batch?.guid || '', data)
      action = 'updat'
    }
    func(data)
      .then((newBatch: Batch) => {
        handleClose()
        onSave?.(newBatch)
        toast.success(() => (
          <Notification
            message={`Successfully ${action}ed Batch: ${newBatch.name}`}
            newObject={newBatch}
          />
        ))
      })
      .catch((error: AxiosError) => {
        handleErrorMessage(error, action, 'Batch')
        setApiErrors(formData.setError, error)
      })
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <Form formProps={{ className: classes.form }} onSubmit={onSubmit}>
        <DialogTitle>{update ? 'Update' : 'Create'} Batch</DialogTitle>
        <DialogContent>
          <Grid container spacing={4}>
            <Grid item xs={12}>
              <MapSelectField
                name="batch_type"
                label="Type"
                defaultValue={batch?.batchType || Batch.Type.batch}
                options={{
                  [Batch.Type.batch]: 'Batch',
                  [Batch.Type.lot]: 'Lot',
                  [Batch.Type.po]: 'Purchase Order',
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField name="client_uid" label="Number" defaultValue={batch?.clientId} />
            </Grid>
            <Grid item xs={12}>
              <DateField name="expires_on" label="Date Expires" defaultValue={batch?.expiresOn} />
            </Grid>
            { allowSelectASN && <Grid item xs={12}>
                <QuerySelectField
                  name="asn"
                  label="ASN"
                  paginator={asnPaginator}
                  defaultValue={batch?.asn}
                />
              </Grid> 
            }
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button type="submit" color="primary" variant="contained" autoFocus>
            Save
          </Button>
        </DialogActions>
      </Form>
    </Dialog>
  )
}
